/**
 * Route chunk prefetching.
 *
 * `Drawer/Routes.tsx` loads all 102 page components with `React.lazy`, so each
 * one is a separate webpack chunk that is not fetched until its route is first
 * visited. That is the right trade for a bundle that was otherwise 5.8 MB, but
 * on its own it moves the wait rather than removing it: the first click on
 * "Clients" now pays for a chunk download before the page can mount.
 *
 * This module closes that gap by starting the download earlier, while the user
 * is still deciding:
 *
 *   prefetchRoute(path)   called on menu hover/focus — by the time the click
 *                         lands, ~200-300ms of hover has usually already
 *                         fetched the chunk, so the route renders immediately.
 *
 *   prefetchCoreRoutes()  called once after the shell mounts, on idle — warms
 *                         the handful of routes essentially every session
 *                         visits.
 *
 * Rules this deliberately follows:
 *
 *   - It only ever fetches *code*, never data. No API request is issued here,
 *     so nothing can be prefetched into a stale cache and no endpoint sees
 *     extra load.
 *   - Each chunk is fetched at most once per page load (`inFlight`), and
 *     webpack itself dedupes beyond that, so hovering a menu item repeatedly
 *     costs nothing.
 *   - It backs off on metered or slow connections (`saveData`, 2g), where
 *     speculative downloading is a cost rather than a saving.
 *   - A failed prefetch is swallowed. This is an optimisation; if the chunk
 *     does not arrive early, `React.lazy` fetches it on navigation exactly as
 *     it would have anyway.
 *
 * The import paths below must match the ones in `Drawer/Routes.tsx` exactly —
 * webpack keys chunks by resolved module, so a mismatched path would fetch a
 * second copy instead of warming the one the route will use.
 */

type Importer = () => Promise<unknown>;

/**
 * Path -> chunk loader, for the routes reachable from the sidebar. Only the
 * destinations a user actually navigates to from the menu are listed; deep
 * links (edit/view/PDF screens) are left to load on demand, which is what
 * keeps this from quietly re-downloading the whole app.
 */
const ROUTE_CHUNKS: Record<string, Importer> = {
  "/dashboard": () => import("../Pages/Dashboard"),
  "/customer/list": () => import("../Pages/Customer/List"),
  "/policy-list": () => import("../Pages/PolicyList/List"),
  "/policy-scenarios/list": () => import("../Pages/PolicyScenarios/List up/index"),
  "/client/meeting": () => import("../Pages/Meeting/List/index"),
  "/client/task": () => import("../Pages/Task/List/index"),
  "/conversations/list": () => import("../Pages/ToDo/List"),
  "/client/newsletters": () => import("../Pages/Newsletters/Type/index"),
  "/workshop/list": () => import("../Pages/Workshop/List"),
  "/client/report/generate": () => import("../Pages/SummaryReport/index"),
  "/policy/list": () => import("../Pages/Product/List"),
  "/category/list": () => import("../Pages/Category/List"),
  "/brand/list": () => import("../Pages/ProductBrand/List"),
  "/client_type/list": () => import("../Pages/ClientType/List"),
  "/team/list": () => import("../Pages/Staff/List"),
  "/department/list": () => import("../Pages/Department/List"),
  "/position/list": () => import("../Pages/Position/List"),
  "/user/list": () => import("../Pages/User/List"),
  "/user/role/list": () => import("../Pages/UserRole/List"),
  "/user/privilege/list": () => import("../Pages/Privilege/List"),
  "/setting": () => import("../Pages/Setting/index"),
  "/email/setting/list": () => import("../Pages/EmailSetting/List"),
  "/documentation": () => import("../Pages/Documentation/index"),
};

const inFlight = new Set<string>();

/**
 * Speculative downloading is a net loss on a metered or genuinely slow link —
 * it competes with the request the user is actually waiting on.
 */
const shouldPrefetch = (): boolean => {
  const connection = (navigator as any)?.connection;
  if (!connection) return true;
  if (connection.saveData) return false;
  const effectiveType: string | undefined = connection.effectiveType;
  if (effectiveType && /(^|-)2g$/.test(effectiveType)) return false;
  return true;
};

/** Warm the chunk behind `path`. Safe to call on every mouse move. */
export const prefetchRoute = (path: string | undefined): void => {
  if (!path || inFlight.has(path)) return;
  const load = ROUTE_CHUNKS[path];
  if (!load || !shouldPrefetch()) return;
  inFlight.add(path);
  /* Swallowed on purpose: React.lazy will retry on navigation. */
  void load().catch(() => {
    inFlight.delete(path);
  });
};

/**
 * The routes almost every session opens. Called once after the app shell
 * mounts, deferred to idle so it never competes with the dashboard's own
 * requests for bandwidth or main-thread time.
 */
const CORE_ROUTES = ["/customer/list", "/policy-list", "/dashboard"];

export const prefetchCoreRoutes = (): (() => void) => {
  if (!shouldPrefetch()) return () => {};

  const run = () => CORE_ROUTES.forEach(prefetchRoute);

  const ric = (window as any).requestIdleCallback as
    | ((cb: () => void, opts?: { timeout: number }) => number)
    | undefined;

  if (ric) {
    const handle = ric(run, { timeout: 4000 });
    return () => (window as any).cancelIdleCallback?.(handle);
  }

  /* Safari has no requestIdleCallback; a timeout past first paint is close
     enough for something this cheap. */
  const handle = window.setTimeout(run, 2500);
  return () => window.clearTimeout(handle);
};
